/** Bulk command labels that name the entity, so ⌘K reads the same wording the selection would. */
import { OutlineEntityKinds } from '../outline.authoring.model';
import { isOutlineRootTask, isOutlineServiceLine, isOutlineSubtask } from '../outline.selection';

/** Only the shape the predicates read — callers pass outline rows, tests pass literals. */
export interface OutlineLabelRow {
  sourceEntity?: unknown;
  parentId?: string;
}

export interface OutlineBulkSelectionShape {
  lines: number;
  rootTasks: number;
  subtasks: number;
}

export function outlineBulkSelectionShape(rows: readonly OutlineLabelRow[] | null | undefined, kinds: OutlineEntityKinds): OutlineBulkSelectionShape {

  const selection = rows ?? [];
  return {
    lines: selection.filter(row => isOutlineServiceLine(row as never, kinds)).length,
    rootTasks: selection.filter(row => isOutlineRootTask(row as never, kinds)).length,
    subtasks: selection.filter(row => isOutlineSubtask(row as never, kinds)).length,
  };
}

/**
 * Every variant keeps the word "Delete": `shift+backspace` resolves this command through
 * `resolveCommandSemanticId`, which matches `\bdelete\b` across id and label for a command-kind
 * bulk descriptor. Renaming it to "Remove …" would silently take the chord away.
 *
 * A selection spanning both halves of the tree gets the neutral "Delete selection" — naming either
 * entity there would understate what the confirm is about to remove.
 */
export function outlineDeleteLabel(shape: OutlineBulkSelectionShape): string {

  const tasks = shape.rootTasks + shape.subtasks;
  if (shape.lines && tasks) return 'Delete selection';
  if (shape.lines) return shape.lines === 1 ? 'Delete service line' : 'Delete service lines';
  // Root tasks win a mixed task selection: a subtask deleted alongside its parent is subsumed by it.
  if (shape.rootTasks) return shape.rootTasks === 1 && !shape.subtasks ? 'Delete task' : 'Delete tasks';
  if (shape.subtasks) return shape.subtasks === 1 ? 'Delete subtask' : 'Delete subtasks';
  return 'Delete selection';
}

/**
 * What the bulk Command K is acting on, for its scope chip: "1 service line", "3 tasks",
 * "1 service line · 2 tasks". Subtasks only count separately when no root task is selected, matching
 * the delete label: a subtask under a selected parent rides with it.
 */
export function outlineSelectionSummary(shape: OutlineBulkSelectionShape): string {

  const parts: string[] = [];
  if (shape.lines) parts.push(`${shape.lines} service line${shape.lines === 1 ? '' : 's'}`);
  const tasks = shape.rootTasks ? shape.rootTasks + shape.subtasks : 0;
  if (tasks) parts.push(`${tasks} task${tasks === 1 ? '' : 's'}`);
  else if (shape.subtasks) parts.push(`${shape.subtasks} subtask${shape.subtasks === 1 ? '' : 's'}`);
  return parts.join(' · ');
}
